import { FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  useCreateAdvertiserShortMutation,
  useGetHoldingCompaniesQuery,
} from "../../api/advertiserAPI";
import Form from "../../components/Form/Form";
import { ValidatorsE } from "../../components/Form/yupValidations";
import { FormFieldT, SelectOptionT } from "../../models/models";
import { useAppDispatch } from "../../store/hooks";
import { setSelectedAdvertiser } from "../../store/slices/deliveryWizardSlice";
import { TOAST_DURATION } from "../../utils/helpers/constants";
import usePopup from "./../../utils/hooks/usePopup";
import { useTranslate } from "../../utils/hooks/useTranslate";

type CreateNewAdvertiserShortBodyPropTypes = {
  advertiserName?: string;
};

const CreateNewAdvertiserShortBody: FC<
  CreateNewAdvertiserShortBodyPropTypes
> = ({ advertiserName }) => {
  const { data = [] } = useGetHoldingCompaniesQuery({});
  const [holdingCompanyInput, setHoldingCompanyInput] = useState(null);
  const [selectedHoldingCompany, setSelectedHoldingCompany] =
    useState<SelectOptionT>();
  const [isSelectedExistingCampaign, setIsSelectedExistingCampaign] =
    useState(false);
  const [isInputChanged, setIsInputChanged] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslate();

  // holding company list with automatically created option based on input value
  const holdingCompanySO = useMemo(() => {
    return holdingCompanyInput
      ? [...data, { value: -1, label: holdingCompanyInput }]
      : data;
  }, [data, holdingCompanyInput]);

  const fields: FormFieldT[] = useMemo(
    () => [
      {
        label: "Name in Hebrew",
        name: "name",
        validation: ValidatorsE.BRAND_NAME,
        defaultValue: advertiserName,
      },
      {
        label: "Name in English",
        name: "alias",
        validation: ValidatorsE.BRAND_NAME,
      },
      {
        label: "Holding company",
        name: "holdingCompanyId",
        selectOptions: holdingCompanySO,
        validation: selectedHoldingCompany
          ? undefined
          : ValidatorsE.SELECT_REQUIRED,
        onInput: (value) => {
          setHoldingCompanyInput(value);
          setIsInputChanged(true);
        },
        onChange: (value) => {
          setSelectedHoldingCompany(value);
          setIsSelectedExistingCampaign(true);
        },
        onBlur: () => {
          if (!isSelectedExistingCampaign && isInputChanged) {
            setSelectedHoldingCompany({
              value: -1,
              label: holdingCompanyInput,
            });
          }
          setIsSelectedExistingCampaign(false);
          setIsInputChanged(false);
        },
        controlledValue: selectedHoldingCompany,
      },
    ],
    [
      holdingCompanySO,
      advertiserName,
      selectedHoldingCompany,
      isSelectedExistingCampaign,
    ]
  );

  const [createAdvertiser] = useCreateAdvertiserShortMutation();
  const { removePopup } = usePopup();

  useEffect(() => {
    const nameField = document.getElementsByName("name")?.[0];

    if (nameField) {
      nameField.focus();
    }
  }, []);

  const handleSubmit = async (data: any) => {
    const body = {
      HoldingCompany: selectedHoldingCompany.label.trim(),
      Id: null,
      Name: data.name.trim(),
      NameAlias: data.alias.trim(),
    };

    try {
      const res: any = await createAdvertiser(body);
      if (!res.error) {
        dispatch(
          setSelectedAdvertiser({
            value: res.data.Id,
            label: res.data.Name,
            shortName: res.data.ShortName,
          })
        );
        removePopup();
        toast("Successfully created", {
          type: "success",
          autoClose: TOAST_DURATION,
        });
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  return (
    <>
      <Form
        fields={fields}
        formID="create-advertiser-short"
        onSubmit={handleSubmit}
        columns={1}
      />
    </>
  );
};

export default CreateNewAdvertiserShortBody;
